import { Input } from "@twilio-paste/core/input";
import { Label } from "@twilio-paste/core/label";
import { Box } from "@twilio-paste/core/box";
import { TextArea } from "@twilio-paste/core/textarea";
import { FormEvent } from "react";
import { Button } from "@twilio-paste/core/button";
import { useDispatch, useSelector } from "react-redux";
import { Text } from "@twilio-paste/core/text";

import { sessionDataHandler } from "../sessionDataHandler";
import { addNotification, changeEngagementPhase, updatePreEngagementData } from "../store/actions/genericActions";
import { initSession } from "../store/actions/initActions";
import { AppState, EngagementPhase } from "../store/definitions";
import { Header } from "./Header";
import { notifications } from "../notifications";
import { NotificationBar } from "./NotificationBar";
import { introStyles, fieldStyles, formStyles, disclaimerStyles } from "./styles/PreEngagementFormPhase.styles";

export const PreEngagementFormPhase = () => {
    const { name, lastName, email, phone, zipCode, modelNumber, serialNumber, query } =
        useSelector((state: AppState) => state.session.preEngagementData) || {};
    const dispatch = useDispatch();

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        dispatch(changeEngagementPhase({ phase: EngagementPhase.Loading }));
        try {
            const data = await sessionDataHandler.fetchAndStoreNewSession({
                formData: {
                    friendlyName: name,
                    lastName,
                    email,
                    phone,
                    zipCode,
                    modelNumber,
                    serialNumber,
                    query
                }
            });
            dispatch(initSession({ token: data.token, conversationSid: data.conversationSid }));
        } catch (err) {
            dispatch(addNotification(notifications.failedToInitSessionNotification((err as Error).message)));
            dispatch(changeEngagementPhase({ phase: EngagementPhase.PreEngagementForm }));
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSubmit(e);
        }
    };

    return (
        <>
            <Header />
            <NotificationBar />
            <Box as="form" data-test="pre-engagement-chat-form" onSubmit={handleSubmit} {...formStyles}>
                <Text {...introStyles} as="p" style={introStyles.style}>
                    Thank you for contacting Philips. For faster service, please fill the form and have your product on-hand.
                </Text>
                <Box {...fieldStyles}>
                    <Label htmlFor="name">* First Name</Label>
                    <Input
                        type="text"
                        placeholder=""
                        name="name"
                        data-test="pre-engagement-chat-form-name-input"
                        value={name}
                        onChange={(e) => dispatch(updatePreEngagementData({ name: e.target.value }))}
                        required
                    />
                </Box>
                <Box {...fieldStyles}>
                    <Label htmlFor="lastName">* Last Name</Label>
                    <Input
                        type="text"
                        placeholder=""
                        name="lastName"
                        data-test="pre-engagement-chat-form-lastname-input"
                        value={lastName}
                        onChange={(e) => dispatch(updatePreEngagementData({ lastName: e.target.value }))}
                        required
                    />
                </Box>
                <Box {...fieldStyles}>
                    <Label htmlFor="email">* What is your email address?</Label>
                    <Input
                        type="email"
                        placeholder=""
                        name="email"
                        data-test="pre-engagement-chat-form-email-input"
                        value={email}
                        onChange={(e) => dispatch(updatePreEngagementData({ email: e.target.value }))}
                        required
                    />
                </Box>
                <Box {...fieldStyles}>
                    <Label htmlFor="phone">* What is your phone number?</Label>
                    <Input
                        type="tel"
                        placeholder=""
                        name="phone"
                        data-test="pre-engagement-chat-form-phone-input"
                        value={phone}
                        pattern="^(\\+?\\d{1,3}[- ]?)?\\d{10}$"
                        onChange={(e) => dispatch(updatePreEngagementData({ phone: e.target.value }))}
                        required
                    />
                </Box>
                <Box {...fieldStyles}>
                    <Label htmlFor="zipCode">* Zip Code</Label>
                    <Input
                        type="text"
                        placeholder=""
                        name="zipCode"
                        data-test="pre-engagement-chat-form-zipcode-input"
                        value={zipCode}
                        onChange={(e) => dispatch(updatePreEngagementData({ zipCode: e.target.value }))}
                        required
                    />
                </Box>
                <Box {...fieldStyles}>
                    <Label htmlFor="modelNumber"> Product Model Number</Label>
                    <Input
                        type="text"
                        placeholder="Found at the back of unit"
                        name="modelNumber"
                        data-test="pre-engagement-chat-form-model-number-input"
                        value={modelNumber}
                        onChange={(e) => dispatch(updatePreEngagementData({ modelNumber: e.target.value }))}
                    />
                </Box>
                <Box {...fieldStyles}>
                    <Label htmlFor="serialNumber"> Serial Number</Label>
                    <Input
                        type="text"
                        placeholder="16 digit number at back of unit"
                        name="serialNumber"
                        data-test="pre-engagement-chat-form-serial-number-input"
                        value={serialNumber}
                        maxLength={16}
                        onChange={(e) => dispatch(updatePreEngagementData({ serialNumber: e.target.value }))}
                    />
                </Box>
                <Box {...fieldStyles}>
                    <Label htmlFor="query">* How can we assist you?</Label>
                    <TextArea
                        placeholder="Brief description of your concern"
                        name="query"
                        data-test="pre-engagement-chat-form-query-textarea"
                        value={query}
                        onChange={(e) => dispatch(updatePreEngagementData({ query: e.target.value }))}
                        onKeyPress={handleKeyPress}
                        required
                    />
                </Box>
                <Text as="p" {...disclaimerStyles}>
                    By continuing to chat, you are giving consent that the information will be sent outside of United States for the purpose of providing service. Please take note that your chat may be saved for training and quality purposes. For more information, kindly visit our privacy policy.
                </Text>

                <Box style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button variant="primary" type="submit" data-test="pre-engagement-start-chat-button">
                        Start chat
                    </Button>
                </Box>
            </Box>
        </>
    );
};
